import type { RumBeforeSend } from "@climatepartner/app-shell/monitoring";

import type { OrganisationContext } from "../auth/use-organisation";

/**
 * The customer organisation a `/customer/:customerId` page shows. AppShell's `@usr.account` is the
 * Frontegg account the user is logged in to, which differs for staff and for parent accounts.
 */
export type ViewedAccount = {
  slug: string;
  name: string;
  is_test: boolean;
  access: "own" | "sub-account" | "internal";
};

type ViewedAccountInput = {
  organisation: OrganisationContext | undefined;
  activeAccountId: string;
  subAccountIds: string[];
  isEmployeeInternal: boolean;
};

const toAccess = ({
  organisation,
  activeAccountId,
  subAccountIds,
  isEmployeeInternal,
}: ViewedAccountInput & { organisation: { id: string } }): ViewedAccount["access"] | undefined => {
  if (organisation.id === activeAccountId) return "own";
  if (subAccountIds.includes(organisation.id)) return "sub-account";
  if (isEmployeeInternal) return "internal";
  return undefined;
};

export const toViewedAccount = (input: ViewedAccountInput): ViewedAccount | undefined => {
  const { organisation } = input;
  if (typeof organisation !== "object" || organisation.id === "") return undefined;

  const access = toAccess({ ...input, organisation });
  if (!access) return undefined;

  return { slug: organisation.id, name: organisation.name, is_test: organisation.sandbox, access };
};

let viewedAccount: ViewedAccount | undefined;

export const setViewedAccount = (account: ViewedAccount | undefined): void => {
  viewedAccount = account;
};

export const addViewedAccount: RumBeforeSend = (event) => {
  if (viewedAccount) {
    event.context = { ...event.context, viewed_account: viewedAccount };
  }
  return true;
};
